// Honest number formatting shared by every marketing KPI surface (campaign hub, list, Analytics, the
// Reports widget, Leads, Bio Page insights). Three rules, one place:
//   - a share always reads "X of Y"; a percentage only on a base of at least 20, whole, never above 100%;
//   - a change vs a previous period is a percentage only when both periods are at least 10, otherwise the
//     plain difference ("+2"), so going from 1 to 3 never reads "+200%".
// Pure — no I/O, no dates. Tested in isolation.
// ---------------------------------------------------------------------------

const COUNT = new Intl.NumberFormat('en-US');

/** Smallest base a percentage is shown on: below it a share is just "X of Y". */
export const SAFE_SHARE_MIN_BASE = 20;

/** Smallest value both periods need before a change is shown as a percentage. */
export const DELTA_MIN_BASE = 10;

/** "7 of 31" (grouped: "1,240 of 3,100"); negatives and fractions clamp to whole non-negative counts. */
export function formatCountOf(count: number, base: number): string {
  return `${COUNT.format(wholeCount(count))} of ${COUNT.format(wholeCount(base))}`;
}

export interface SafeShare {
  /** "7 of 31" — always shown. */
  text: string;
  /** Whole percent in [0, 100]; null when the base is below `minBase` (a % on a tiny base misleads). */
  pct: number | null;
}

/**
 * An honest share of people: "X of Y" always, a percentage only on a base of at least `minBase`, and never
 * above 100% (a count can outgrow its base when the two come from different reads).
 */
export function safeShare(count: number, base: number, options: { minBase?: number } = {}): SafeShare {
  const minBase = options.minBase ?? SAFE_SHARE_MIN_BASE;
  const safeCount = wholeCount(count);
  const safeBase = wholeCount(base);
  const pct = safeBase > 0 && safeBase >= minBase ? Math.min(100, Math.round((safeCount / safeBase) * 100)) : null;
  return { text: formatCountOf(safeCount, safeBase), pct };
}

export type MetricDeltaTone = 'up' | 'down' | 'flat';

export interface MetricDelta {
  /** "+18%" / "−12%" / "0%" on a big enough base, else the plain difference "+2" / "−1" / "0". */
  text: string;
  tone: MetricDeltaTone;
  /** `pct` when both periods reach the minimum base; `diff` when the text is a plain difference. */
  kind: 'pct' | 'diff';
}

/**
 * Change vs the previous period. A whole-percent change only when BOTH periods are at least `minBase`;
 * otherwise the plain difference. null when there is nothing to compare (no previous period, or zero both
 * times). Uses a true minus sign so "−12%" lines up with "+18%".
 */
export function formatDelta(
  current: number,
  previous: number | null,
  options: { minBase?: number } = {}
): MetricDelta | null {
  if (previous === null || !Number.isFinite(previous) || !Number.isFinite(current)) return null;
  if (current === 0 && previous === 0) return null;
  const minBase = options.minBase ?? DELTA_MIN_BASE;
  if (current >= minBase && previous >= minBase) {
    const pct = Math.round(((current - previous) / previous) * 100);
    return { text: signed(pct, '%'), tone: toneOf(pct), kind: 'pct' };
  }
  const diff = Math.round(current - previous);
  return { text: signed(diff, ''), tone: toneOf(diff), kind: 'diff' };
}

function wholeCount(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
}

function signed(value: number, unit: string): string {
  if (value > 0) return `+${COUNT.format(value)}${unit}`;
  if (value < 0) return `−${COUNT.format(Math.abs(value))}${unit}`;
  return `0${unit}`;
}

function toneOf(value: number): MetricDeltaTone {
  return value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
}
